import cart from 'lib/cart'
import { async } from 'regenerator-runtime'

/**
 * @info
 *
 * *The "store/cart" directory*
 * This "store" directory declares each of the Vuex
 * modules that all together power the site. Here, we have the
 * Vuex store module that sits behind cart functionality.
 *
 * *This file*
 * This file outlines "actions". Actions are functions that
 * include logic that, when ready, commits a mutation to the
 * application state. Whereas mutations must be synchronous,
 * actions return a promise and can include asynchronous
 * logical sequences.
 */
export default {
  getCart ({ commit, state, dispatch }, payload) {
    commit('setLoading', true)

    return cart
      .get()
      .then(data => {
        commit('global/refreshCart', data, { root: true })
        commit('setLoading', false)
        dispatch('fetchDeliveryData')
      })
  },

  /**
   * Pulls despatch data for the current cart from the `cart-delivery` section.
   * Rendered server-side because the AJAX Cart API exposes neither inventory
   * levels nor product metafields. Failures are non-fatal: the drawer simply
   * renders without the delivery line.
   */
  fetchDeliveryData ({ commit }) {
    // `no-store` keeps inventory levels current: the date itself is computed
    // from the client clock, but a cached payload could report stale stock.
    return fetch(`${window.location.pathname}?section_id=cart-delivery`, {
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(response => (response.ok ? response.text() : Promise.reject(response.status)))
      .then(html => {
        const holder = document.createElement('div')
        holder.innerHTML = html

        const payload = holder.querySelector('[data-cart-delivery]')
        if (!payload) {
          return
        }

        commit('setDeliveryData', JSON.parse(payload.textContent))
      })
      .catch(() => {})
  },

  updateItemQuantity ({ commit, state, dispatch }, { quantity, id, properties }) {
    if (!id) {
      return
    }

    commit('setLoading', true)

    return cart
      .update({ quantity, id, properties })
      .then(data => {
        return dispatch('getCart', data)
          .then(() => {
            commit('setLoading', false)
            commit('setCart', true)
          })
      })
  },

  openCart ({ commit }) {
    commit('setCart', true)
  },

  toggleCart ({ commit }) {
    commit('toggleCart')
  },

  closeCart ({ commit }) {
    commit('closeCart')
  },

  addToCart ({ commit, state, dispatch }, { id, quantity, properties, withProperties = false }) {
    return cart
      .add({ quantity, id, properties })
      .then(data => {
        return dispatch('getCart', data)
          .then(() => {
            commit('setLoading', false)
            commit('setCart', true)
          })
      })
  },

  // Sync packaging selection:
  // - type: 'free' | 'premium' | null (null = deselect all)
  // Removes any existing packaging line item then adds the chosen one.
  // Free has price 0 but is still a line item (same as premium).
  syncPackaging ({ commit, state, dispatch }, type) {
    const options = __GLOBAL__.packagingOptions
    const freeId = options && options.free && options.free.id
    const premiumId = options && options.premium && options.premium.id

    commit('setLoading', true)

    return cart.get().then(cartData => {
      // Find any existing packaging line item (free or premium)
      const existingItem = cartData.items.find(item =>
        (freeId && item.variant_id === freeId) ||
        (premiumId && item.variant_id === premiumId)
      )

      const removeExisting = existingItem
        ? cart.change({ id: existingItem.key, quantity: 0 })
        : Promise.resolve()

      return removeExisting.then(() => {
        const targetId = type === 'free' ? freeId : type === 'premium' ? premiumId : null

        if (!targetId) {
          return dispatch('getCart').then(() => commit('setLoading', false))
        }

        return cart.add({ id: targetId, quantity: 1 }).then(() => {
          return dispatch('getCart').then(() => commit('setLoading', false))
        })
      })
    })
  }
}
